import { defineStore } from 'pinia'
import { isUnifiedCommerce } from '@shared/constants'
import { checkPermissions } from '@shared/utils/permissions'
import { useAuthStore } from '@shared/store/auth'
import th from '@tillhub/javascript-sdk'

export const allBranches = { id: null, label: '_all' }

export const allBranchesContext = {
  branches: [allBranches],
  location: null
}

function merchantFeaturesFrom(features = {}) {
  return {
    moto: features.moto ?? false,
    ecom: features.ecom ?? false,
    linkpay: features.linkpay ?? false,
    paylater: features.paylater ?? false
  }
}

export const useLocationStore = defineStore('location', {
  // Persist this store to localStorage (via pinia-plugin-persistedstate plugin)
  persist: {
    // Persist only the necessary fields to ensure we don't get localStorage QuotaExceededError (see UNTIL-18230).
    pick: ['currentLocation', 'locationContext']
  },

  state: () => ({
    locationContext: { branches: allBranchesContext.branches },
    locationContextAll: {},
    locationUnzerId: undefined,
    currentLocation: allBranchesContext.location,
    merchantFeatures: merchantFeaturesFrom(),
    merchantFeaturesFetched: false
  }),

  getters: {
    currentBranchNumber: (state) => {
      return state.locationContext?.branches?.[0]?.custom_id
    },
    currentBranchName: (state) => {
      return state.locationContext?.branches?.[0]?.label
    }
  },

  actions: {
    setLocationContext({ branches, location = null }) {
      this.locationContext = { branches }
      this.locationUnzerId = branches?.[0]?.mms_unit_unzer_id
      this.currentLocation = location
    },

    resetForTenantSwitch() {
      this.setLocationContext({
        branches: allBranchesContext.branches,
        location: allBranchesContext.location
      })
      this.locationContextAll = {}
      this.merchantFeatures = merchantFeaturesFrom()
      this.merchantFeaturesFetched = false
      // Drop cached PayLater key pairs from the previous tenant.
      void import('@uz/views/orders/payment_links/utils/api-key-helpers')
        .then(({ clearPaylaterKeyPairsCache }) => clearPaylaterKeyPairsCache())
        .catch((error) => {
          this.$logException?.(error, { trackError: false })
        })
    },

    // fetch merchant features to control batch-processing nav visibility
    async fetchMerchantFeatures() {
      if (this.merchantFeaturesFetched) return

      try {
        const { data } = await th.ordersV2().features()
        this.merchantFeatures = merchantFeaturesFrom(data?.results[0])
      } catch (error) {
        this.$logException?.(error, { trackError: false })
        this.merchantFeatures = merchantFeaturesFrom()
      } finally {
        this.merchantFeaturesFetched = true
      }
    },

    /**
     * Load branch list when missing (e.g. full reload before the header location
     * picker has run). Reuses the same branchesV1 query shape as location-context.
     */
    async loadBranchesForPaylaterIfNeeded() {
      if (this.locationContextAll?.branches?.length) {
        return
      }

      const authStore = useAuthStore()
      if (
        isUnifiedCommerce() &&
        !checkPermissions(
          { scopes: ['resources:branches:read'] },
          authStore.role,
          authStore.scopes
        )
      ) {
        return
      }

      const query = { limit: 100, deleted: false }
      if (isUnifiedCommerce()) {
        query.extended = 'true'
      }
      const collected = []
      let batch = await th.branchesV1().getAll({ query })
      collected.push(...(batch.data || []))
      while (batch.next) {
        batch = await batch.next()
        collected.push(...(batch.data || []))
      }
      this.locationContextAll = { branches: collected }
    }
  }
})
